/**
 * Shared motion tokens for register terms accordion + keyboard settle sequencing.
 * One open/close pair keeps expand/collapse reversible without fighting layout transitions.
 */
import { Keyboard, Platform } from 'react-native';
import { Easing } from 'react-native-reanimated';

export const REGISTER_TERMS_MOTION = {
  /** Expand duration (ms). Close uses the same so reverse is a perfect loop. */
  durationMs: 320,
  /** Soft deceleration — avoids cubic snap at the end. */
  easing: Easing.bezier(0.22, 1, 0.36, 1),
  /** Max wait for keyboard dismiss before expanding (ms). */
  keyboardSettleMaxMs: 420,
  /** Extra beat after keyboard hide so panel lift can ease to rest. @deprecated use liftOnlySettleMs after hide */
  afterKeyboardSettleMs: 48,
  /** Panel lift ease-down (keep in sync with AppOverlay lift timing). */
  liftOnlySettleMs: 280,
} as const;

export function waitMs(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Resolves on keyboard fully hidden, or immediately if already hidden. */
export function waitKeyboardHidden(max_ms = REGISTER_TERMS_MOTION.keyboardSettleMaxMs): Promise<void> {
  return new Promise(resolve => {
    if (!Keyboard.isVisible()) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      sub.remove();
      resolve();
    };

    const event = Platform.OS === 'ios' ? 'keyboardDidHide' : 'keyboardDidHide';
    const sub = Keyboard.addListener(event, finish);
    setTimeout(finish, max_ms);
  });
}

export type RegisterTermsSettleInput = {
  /** True when the auth card was lifted for a focused field. */
  panel_lift_px: number;
  /** Extra scroll pad from keyboard avoidance. */
  keyboard_scroll_pad: number;
  clear_panel_offset: () => void;
};

/**
 * Phase A before terms expand:
 * dismiss keyboard → clear lift/pad → wait until form is at rest.
 * Does not start Phase B (accordion); caller animates after this resolves.
 */
export async function settleRegisterFormBeforeTerms(
  input: RegisterTermsSettleInput,
): Promise<'settled' | 'already_resting'> {
  const kb_open = Keyboard.isVisible();
  const needs_settle = kb_open || input.panel_lift_px > 0 || input.keyboard_scroll_pad > 0;

  if (!needs_settle) {
    return 'already_resting';
  }

  if (kb_open) {
    Keyboard.dismiss();
  }
  input.clear_panel_offset();

  if (kb_open) {
    await waitKeyboardHidden();
    await waitMs(REGISTER_TERMS_MOTION.liftOnlySettleMs);
  } else {
    await waitMs(REGISTER_TERMS_MOTION.liftOnlySettleMs);
  }

  return 'settled';
}
